import React, { useState } from "react";
import { Card, CardBody, Row, Col, Button, Input, Spinner } from "reactstrap";
import { Avatar } from "antd";
import {
  UserOutlined,
  EditOutlined,
  EyeInvisibleTwoTone,
  EyeTwoTone,
} from "@ant-design/icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateMarchandById } from "../../services/merchandApi";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import FormField from "../../../../common/Form/FormField";
import OTPCodeVerify from "../../../../common/otp/OTPCodeVerify";
import { updatePassword } from "../../../auth/services/authApi";

const ProfileDetail = ({ currentuser }) => {
  
  const user = currentuser?.user || {};
  const merchandId = user?.id;
  const queryClient = useQueryClient();

  const [editingInfos, setEditingInfos] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [passwordPayload, setPasswordPayload] = useState(null);
  const [errorUpdate, setErrorUpdate] = useState(null);

  const formikInfos = useFormik({
    initialValues: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      adresse: user.adresse || "",
      phoneNumber: user.phoneNumber || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Prénom requis"),
      lastName: Yup.string().required("Nom requis"),
      adresse: Yup.string().required("Adresse requise"),
      phoneNumber: Yup.string().required("Téléphone requis"),
    }),
    onSubmit: async (values) => {
      const data = { ...values};
      setIsLoadingSubmit(true);
      try {
        await updateMarchandById(merchandId, data);
        toast.success("Informations mises à jour");
        await queryClient.invalidateQueries("authUser");
        setEditingInfos(false);
      } catch (error) {
        toast.error(error || "Erreur lors de la mise à jour.");
      } finally {
        setIsLoadingSubmit(false);
      }
    },
    enableReinitialize: true,
  });

  const formikPassword = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Ancien mot de passe requis"),
      newPassword: Yup.string()
        .min(8, "Minimum 8 caractères")
        .matches(/[A-Z]/, "Doit contenir une majuscule")
        .matches(/[0-9]/, "Doit contenir un chiffre")
        .required("Nouveau mot de passe requis"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Les mots de passe doivent correspondre")
        .required("Confirmation obligatoire"),
    }),
    onSubmit: async (values) => {
      setPasswordPayload({
        email: user?.email,
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      setShowOtpModal(true);
    },
  });

  const handleOtpVerified = async () => {
    setIsLoadingSubmit(true);
    try {
      await updatePassword(passwordPayload);
      toast.success("Mot de passe mis à jour 🎉");
      setEditingPassword(false);
      formikPassword.resetForm();
    } catch (error) {
      setErrorUpdate(error || "Une erreur est survenue");
      toast.error(error || "Échec de la mise à jour du mot de passe");
    } finally {
      setIsLoadingSubmit(false);
      setShowOtpModal(false);
    }
  };

  return (
    <Card className="shadow-sm border-0 rounded-lg">
      <CardBody className="p-4">
        <h5 className="mb-4 fw-bold">Mon profil</h5>

        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <Avatar size={64} icon={<UserOutlined />} />
            <div>
              <h6 className="mb-0">
                {formikInfos.values.firstName} {formikInfos.values.lastName}
              </h6>
              <p className="text-muted mb-0">{currentuser?.roles || "-"}</p>
              <small className="text-muted">{currentuser?.email || "-"}</small>
            </div>
          </div>
        </div>

        <hr />

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-semibold text-muted">Informations personnelles</h6>
          {!editingInfos && (
            <Button size="sm" type="link" onClick={() => setEditingInfos(true)}>
              <EditOutlined /> Éditer
            </Button>
          )}
        </div>

        <form onSubmit={formikInfos.handleSubmit}>
          <Row className="mb-1 text-muted fw-semibold">
            <Col md={3}>Prénom</Col>
            <Col md={3}>Nom</Col>
            <Col md={3}>Adresse</Col>
            <Col md={3}>Téléphone</Col>
          </Row>

          <Row className="align-items-center mb-3">
            {["firstName", "lastName", "adresse", "phoneNumber"].map((field) => (
              <Col md={3} key={field}>
                {editingInfos ? (
                  <>
                    <Input
                      name={field}
                      value={formikInfos.values[field]}
                      onChange={formikInfos.handleChange}
                      onBlur={formikInfos.handleBlur}
                      className={
                        formikInfos.touched[field] && formikInfos.errors[field]
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formikInfos.touched[field] && formikInfos.errors[field] && (
                      <div className="text-danger small">{formikInfos.errors[field]}</div>
                    )}
                  </>
                ) : (
                  <span>{formikInfos.values[field]}</span>
                )}
              </Col>
            ))}
          </Row>

          {editingInfos && (
            <div className="text-end">
              <Button
                className="me-2"
                color="secondary"
                onClick={() => setEditingInfos(false)}
                disabled={isLoadingSubmit}
              >
                Annuler
              </Button>
              <Button color="primary" type="submit" disabled={isLoadingSubmit}>
                {isLoadingSubmit ? <Spinner size="sm" /> : "Sauvegarder"}
              </Button>{" "}
              
            </div>
          )}
        </form>

        <hr />
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-semibold text-muted">Sécurité</h6>
          {!editingPassword && (
            <Button size="sm" type="link" onClick={() => setEditingPassword(true)}>
              <EditOutlined /> Éditer
            </Button>
          )}
        </div>

        <form onSubmit={formikPassword.handleSubmit} disabled={isLoadingSubmit}>
          <Row>
            <Col md={3} className="text-muted">Mot de passe</Col>
            <Col md={9}>
              {editingPassword ? (
                <Row className="mt-2">
                  {errorUpdate && <div className="alert alert-danger">{errorUpdate}</div>}
                  <FormField
                    label="Ancien mot de passe"
                    name="oldPassword"
                    formik={formikPassword}
                    type={showPassword ? "text" : "password"}
                    colSize={12}
                    icon={
                      showPassword ? (
                        <EyeInvisibleTwoTone
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <EyeTwoTone onClick={() => setShowPassword(true)} />
                      )
                    }
                  />

                  <FormField
                    label="Nouveau mot de passe"
                    name="newPassword"
                    formik={formikPassword}
                    type={showPassword ? "text" : "password"}
                    colSize={12}
                    icon={
                      showPassword ? (
                        <EyeInvisibleTwoTone
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <EyeTwoTone onClick={() => setShowPassword(true)} />
                      )
                    }
                  />

                  <FormField
                    label="Confirmer le mot de passe"
                    name="confirmPassword"
                    formik={formikPassword}
                    type={showConfirm ? "text" : "password"}
                    colSize={12}
                    icon={
                      showConfirm ? (
                        <EyeInvisibleTwoTone
                          onClick={() => setShowConfirm(false)}
                        />
                      ) : (
                        <EyeTwoTone onClick={() => setShowConfirm(true)} />
                      )
                    }
                  />
                </Row>
              ) : (
                <strong>************</strong>
              )}
            </Col>
          </Row>

          {editingPassword && (
            <div className="text-end mt-3">
              <Button
                className="me-2"
                color="secondary"
                onClick={() => setEditingPassword(false)}
                disabled={isLoadingSubmit}
              >
                Annuler
              </Button>
              <Button color="success" type="submit" disabled={isLoadingSubmit}>
                {isLoadingSubmit ? <Spinner size="sm" /> : "Modifier"}
              </Button>{" "}
              
            </div>
          )}
        </form>
      </CardBody>

      <OTPCodeVerify
        isOpen={showOtpModal}
        title="Vérification OTP"
        message="Chez Paxity, votre sécurité est notre priorité. On vous enverra un code à 6 chiffres à saisir à la prochaine étape."
        onCancel={() => setShowOtpModal(false)}
        onVerified={handleOtpVerified}
      />
    </Card>
  );
};

export default ProfileDetail;
